// Агент: шесть вкладок (разбор 26.09: 7 → 5; 13.10 вернулся канал). Лента — работа агента рассказом; чаты —
// разговоры владельца; задачи (и расписание с очередью — секцией внутри); журнал — все решения с откатами; знания
// (правила, выводы, гипотезы, итоги); канал — Telegram-канал, который ведёт агент (слово владельца 13.10).
// У разговора, который затих, есть итог: агент подводит его сам и записывает договорённости в память.
import { toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  BookOpen,
  ChevronDown,
  ChevronUp,
  ListTodo,
  Megaphone,
  MessagesSquare,
  NotebookPen,
  Rss,
  SquarePen,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type ThreadSummary } from '../api';
import type { AttachmentRef } from '../chat/attach';
import { ChatModelPicker, ChatWelcome } from '../chat/ChatPanel';
import { Composer, Conversation } from '../chat/Conversation';
import { chatStore } from '../chat/store';
import { type ThreadMeta, useChatModel } from '../chat/useChatModel';
import { useChatThread } from '../chat/useChatThread';
import { PageHead } from './common';

const TABS = [
  { to: '/agent/feed', label: 'Лента', icon: Rss },
  { to: '/agent/chats', label: 'Чаты', icon: MessagesSquare },
  { to: '/agent/tasks', label: 'Задачи', icon: ListTodo },
  { to: '/agent/journal', label: 'Журнал', icon: NotebookPen },
  { to: '/agent/knowledge', label: 'Знания', icon: BookOpen },
  { to: '/agent/channel', label: 'Канал', icon: Megaphone },
] as const;

export function AgentLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  // Открытый чат на телефоне — на весь экран: заголовок и вкладки прячет CSS (index.css, «Агент → Чаты»)
  const inThread = /^\/agent\/chats\/[^/]+/.test(path);
  // На телефоне строка вкладок листается: открытая вкладка — целиком в видимой части, а не за краем
  const tabs = useRef<HTMLElement>(null);
  useEffect(() => {
    const row = tabs.current;
    if (!row || !path) return;
    const show = () => {
      const cur = row.querySelector<HTMLElement>('a[aria-current="page"]');
      if (!cur) return;
      const r = row.getBoundingClientRect();
      const c = cur.getBoundingClientRect();
      // Не влезает — в середину строки: соседние вкладки выглядывают, видно, что строка листается
      if (c.left < r.left || c.right > r.right)
        row.scrollLeft += c.left + c.width / 2 - (r.left + r.width / 2);
    };
    show();
    // Ещё раз, когда вкладки меняют ширину: шрифт догружается позже, и открытая уехала бы за край
    const ro = new ResizeObserver(show);
    for (const a of row.querySelectorAll('a')) ro.observe(a);
    return () => ro.disconnect();
  }, [path]);
  return (
    <div
      className="agent-layout flex flex-col"
      style={{ height: '100%' }}
      data-thread={inThread || undefined}
    >
      <PageHead
        title="Агент"
        sub="Claude Code на вашей подписке: работает по расписанию и по событиям, отвечает в чате, ведёт журнал и память"
      />
      <nav className="tabs" aria-label="Разделы агента" ref={tabs}>
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.to} to={t.to} aria-current={path.startsWith(t.to) ? 'page' : undefined}>
              <Icon size={16} />
              {t.label}
            </Link>
          );
        })}
      </nav>
      <Outlet />
    </div>
  );
}

/** Убрать чат в архив или вернуть; в архив — с кнопкой «Вернуть» в тосте (ничего не теряется). */
function useArchive(activeId: string | null) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const refresh = () => void qc.invalidateQueries({ queryKey: ['threads'] });
  const set = async (id: string, archived: boolean) => {
    try {
      await api(`/api/threads/${id}`, { archived }, 'PATCH');
    } catch (e) {
      toast((e as Error).message);
      return;
    }
    refresh();
    if (!archived) {
      toast('Чат вернулся в список');
      return;
    }
    if (id === activeId) void navigate({ to: '/agent/chats' });
    toast('Чат убран в архив', { label: 'Вернуть', run: () => void set(id, false) });
  };
  return set;
}

function ThreadRow({
  t,
  active,
  action,
}: {
  t: ThreadSummary;
  active: boolean;
  action: { label: string; icon: React.ReactNode; run: () => void };
}) {
  return (
    <div className="thread-row" data-active={active || undefined}>
      <Link
        to="/agent/chats/$threadId"
        params={{ threadId: t.id }}
        aria-current={active ? 'page' : undefined}
        className="thread-link"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: '9px 38px 9px 10px',
          borderRadius: 'var(--r)',
          color: 'var(--text)',
          background: active ? 'var(--accent-soft)' : undefined,
        }}
      >
        <span className="flex items-center gap-2" style={{ fontWeight: 500, minWidth: 0 }}>
          {t.working && <span className="status-dot busy" style={{ width: 7, height: 7 }} />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t.title ?? 'Без названия'}
          </span>
        </span>
        <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
          {t.context?.label ? `${t.context.label} · ` : ''}
          {t.lastMessageAt ? when(t.lastMessageAt) : ''}
        </span>
      </Link>
      <button
        type="button"
        className="icon-btn thread-act"
        aria-label={`${action.label}: ${t.title ?? 'Без названия'}`}
        title={action.label}
        onClick={action.run}
      >
        {action.icon}
      </button>
    </div>
  );
}

function ThreadList({ activeId }: { activeId: string | null }) {
  const [showArchive, setShowArchive] = useState(false);
  const q = useQuery({
    queryKey: ['threads'],
    queryFn: () => api<ThreadSummary[]>('/api/threads'),
    refetchInterval: 15_000,
  });
  const archived = useQuery({
    queryKey: ['threads', 'archived'],
    queryFn: () => api<ThreadSummary[]>('/api/threads?archived=1'),
    enabled: showArchive,
  });
  const archive = useArchive(activeId);
  const items = (q.data ?? []).filter((t) => t.lastMessageAt);
  return (
    <aside
      className="flex flex-col"
      style={{
        borderRight: '1px solid var(--border)',
        minHeight: 0,
        overflow: 'auto',
        background: 'var(--surface)',
      }}
    >
      <div style={{ padding: 12 }}>
        <Link
          to="/agent/chats"
          className="btn btn-block"
          style={{ justifyContent: 'flex-start' }}
          onClick={(e) => {
            // На телефоне здесь список чатов — новый разговор открывается в панели чата
            if (!window.matchMedia('(max-width: 760px)').matches) return;
            e.preventDefault();
            chatStore.open({ threadId: null });
          }}
        >
          <SquarePen />
          Новый чат
        </Link>
      </div>
      {q.isPending && (
        <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton" style={{ height: 44 }} />
          ))}
        </div>
      )}
      {q.isSuccess && items.length === 0 && (
        <p className="muted" style={{ padding: '0 16px', fontSize: 'var(--fs-sm)' }}>
          Чатов пока нет.
        </p>
      )}
      <nav className="flex flex-col" style={{ padding: '0 8px 12px', gap: 2 }} aria-label="Чаты">
        {items.map((t) => (
          <ThreadRow
            key={t.id}
            t={t}
            active={t.id === activeId}
            action={{ label: 'Убрать в архив', icon: <Archive />, run: () => void archive(t.id, true) }}
          />
        ))}
      </nav>
      {/* Архив: сюда уходят убранные чаты, пустые и те, где месяц не было сообщений; вернуть — одной кнопкой */}
      <div style={{ marginTop: 'auto', padding: '8px 8px 12px', borderTop: '1px solid var(--border)' }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-block"
          style={{ justifyContent: 'flex-start' }}
          aria-expanded={showArchive}
          onClick={() => setShowArchive((v) => !v)}
        >
          <Archive />
          Архив
          {showArchive ? (
            <ChevronUp style={{ marginLeft: 'auto' }} />
          ) : (
            <ChevronDown style={{ marginLeft: 'auto' }} />
          )}
        </button>
        {showArchive && (
          <nav className="flex flex-col" style={{ gap: 2, marginTop: 4 }} aria-label="Архив чатов">
            {archived.isPending && <div className="skeleton" style={{ height: 44 }} />}
            {archived.isSuccess && archived.data.length === 0 && (
              <p className="muted" style={{ padding: '4px 8px', fontSize: 'var(--fs-sm)', margin: 0 }}>
                В архиве пусто. Сюда уходят чаты, которые вы убрали, и те, где месяц не было сообщений.
              </p>
            )}
            {(archived.data ?? []).map((t) => (
              <ThreadRow
                key={t.id}
                t={t}
                active={t.id === activeId}
                action={{
                  label: 'Вернуть из архива',
                  icon: <ArchiveRestore />,
                  run: () => void archive(t.id, false),
                }}
              />
            ))}
          </nav>
        )}
      </div>
    </aside>
  );
}

/**
 * Итог разговора над лентой: тема одной строкой, по нажатию — что договорились. Агент подводит его после паузы в
 * разговоре и записывает решения и планы в «Знания» — отсюда туда ссылка.
 */
function ThreadSummaryNote({ threadId }: { threadId: string }) {
  const q = useQuery({
    queryKey: ['thread', threadId],
    queryFn: () => api<ThreadMeta>(`/api/threads/${threadId}`),
  });
  const s = q.data?.summary;
  if (!s) return null;
  return (
    <details className="thread-sum">
      <summary>
        <NotebookPen aria-hidden />
        <span className="thread-sum-h">
          <b>Итог разговора</b>
          {s.headline ? <span> · {s.headline}</span> : null}
        </span>
        <ChevronDown className="thread-sum-chev" aria-hidden />
      </summary>
      <p>{s.text}</p>
      <Link to="/agent/knowledge" className="thread-sum-link">
        Что агент запомнил — в «Знаниях»
      </Link>
    </details>
  );
}

/** Шапка открытого чата на телефоне: назад к списку и название разговора. */
function ThreadBar({ threadId }: { threadId: string }) {
  const q = useQuery({
    queryKey: ['threads'],
    queryFn: () => api<ThreadSummary[]>('/api/threads'),
    refetchInterval: 15_000,
  });
  const title = q.data?.find((t) => t.id === threadId)?.title;
  return (
    <div className="thread-bar only-m">
      <Link to="/agent/chats" className="icon-btn" aria-label="Все чаты">
        <ArrowLeft />
      </Link>
      <b>{title ?? 'Чат с агентом'}</b>
    </div>
  );
}

export function ChatsPage() {
  const params = useParams({ strict: false }) as { threadId?: string };
  const threadId = params.threadId ?? null;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const onCreated = useCallback(
    (id: string) => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void navigate({ to: '/agent/chats/$threadId', params: { threadId: id }, replace: true });
    },
    [navigate, qc],
  );
  const t = useChatThread(threadId, onCreated, { kind: 'page', label: 'Агент' });
  const cm = useChatModel(threadId);
  const send = async (text: string, attachments: AttachmentRef[] = []) => {
    await cm.flush();
    return t.send(text, attachments);
  };
  return (
    <div className="chats-grid" data-thread={threadId ? '' : undefined}>
      <div className="chats-list-wrap">
        <ThreadList activeId={threadId} />
      </div>
      <section className="chats-conv flex flex-col" style={{ minHeight: 0 }} aria-label="Разговор">
        {threadId && <ThreadBar threadId={threadId} />}
        {threadId && <ThreadSummaryNote threadId={threadId} />}
        <Conversation
          view={t.view}
          ready={t.ready || !threadId}
          empty={<ChatWelcome onPick={(q) => void send(q)} />}
        />
        <Composer
          working={t.view.working}
          onSend={send}
          onStop={t.stop}
          onFocusInput={() => void t.ensure().catch(() => {})}
          tools={<ChatModelPicker cm={cm} />}
        />
      </section>
      <button type="button" className="sr-only" onClick={() => chatStore.open({ threadId })}>
        Открыть в панели
      </button>
    </div>
  );
}
